import { useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Eye, KeyRound, Pencil, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const STATUS_CHIP = {
  expired: "bg-red-600 text-white",
  expiring_30: "bg-amber-500 text-white",
  expiring_60: "bg-amber-400 text-slate-900",
  expiring_90: "bg-yellow-300 text-slate-900",
  valid: "bg-teal-600 text-white",
};
export const STATUS_LABEL = {
  expired: "EXPIRED", expiring_30: "≤ 30 DAYS", expiring_60: "≤ 60 DAYS",
  expiring_90: "≤ 90 DAYS", valid: "VALID",
};
const PROVENANCE_CHIP = {
  extracted: ["OCR EXTRACTED", "border border-teal-600/50 text-teal-800 bg-teal-50/50"],
  manual: ["MANUAL", "border border-slate-300 text-slate-600"],
  metadata_only: ["METADATA ONLY", "border border-amber-400 text-amber-700 bg-amber-50/50"],
};

export default function RegistryTable({ docs, user, onChanged, onPreview }) {
  const isHR = ["admin", "hr_manager"].includes(user.role);
  const isDeptHead = user.role === "dept_head";
  const [revealed, setRevealed] = useState({});
  const [editDoc, setEditDoc] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteDoc, setDeleteDoc] = useState(null);

  const canReveal = (d) => isHR || (user.role === "employee" && d.employee_id === user.employee_id);
  const canFile = (d) => d.has_file && (isHR || (user.role === "employee" && d.employee_id === user.employee_id));

  const reveal = async (d) => {
    if (revealed[d.id]) { setRevealed((r) => ({ ...r, [d.id]: undefined })); return; }
    try {
      const { data } = await api.get(`/documents/${d.id}/reveal`);
      setRevealed((r) => ({ ...r, [d.id]: data.doc_number }));
      toast.success("Number revealed — audit record written");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const verify = async (d) => {
    try {
      await api.post(`/documents/${d.id}/verify`);
      toast.success(`Verified ${d.title} for ${d.employee_name}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/documents/${editDoc.id}`, editForm);
      toast.success("Document updated");
      setEditDoc(null);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/documents/${deleteDoc.id}`);
      toast.success("Document deleted");
      setDeleteDoc(null);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white border border-border">
      <Table data-testid="documents-table">
        <TableHeader>
          <TableRow className="hover:bg-transparent border-border">
            <TableHead className="text-[11px] uppercase tracking-[0.1em] pl-5">Employee</TableHead>
            <TableHead className="text-[11px] uppercase tracking-[0.1em]">Document</TableHead>
            {!isDeptHead && <TableHead className="text-[11px] uppercase tracking-[0.1em]">Number</TableHead>}
            <TableHead className="text-[11px] uppercase tracking-[0.1em]">Provenance</TableHead>
            <TableHead className="text-[11px] uppercase tracking-[0.1em]">Expiry</TableHead>
            <TableHead className="text-[11px] uppercase tracking-[0.1em]">Status</TableHead>
            <TableHead className="text-[11px] uppercase tracking-[0.1em] text-right pr-5">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {docs.map((d) => {
            const [provLabel, provClass] = PROVENANCE_CHIP[d.provenance] || PROVENANCE_CHIP.manual;
            return (
              <TableRow key={d.id} className="border-border hover:bg-slate-50/70" data-testid={`doc-row-${d.id}`}>
                <TableCell className="pl-5">
                  <p className="text-[13px] font-medium text-slate-800">{d.employee_name}</p>
                  <p className="text-[10px] text-muted-foreground">{d.department}</p>
                </TableCell>
                <TableCell>
                  <p className="text-[13px] text-slate-700">{d.title}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {d.doc_type}
                    {d.verification === "pending_verification" && (
                      <span className="ml-2 text-amber-700 font-semibold">· PENDING VERIFICATION</span>
                    )}
                  </p>
                </TableCell>
                {!isDeptHead && (
                  <TableCell className="font-mono text-[12px] text-slate-500">
                    <span data-testid={`doc-number-${d.id}`}>{revealed[d.id] || d.doc_number || "—"}</span>
                    {d.doc_number && canReveal(d) && (
                      <button onClick={() => reveal(d)} title="Audited reveal" data-testid={`reveal-btn-${d.id}`}
                        className={`ml-2 align-middle ${revealed[d.id] ? "text-teal-700" : "text-slate-400 hover:text-slate-700"}`}>
                        <KeyRound className="h-3.5 w-3.5 inline" />
                      </button>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <span className={`text-[9px] font-mono px-1.5 py-0.5 ${provClass}`}>{provLabel}</span>
                </TableCell>
                <TableCell className="font-mono text-[12px] text-slate-600">{d.expiry_date || "—"}</TableCell>
                <TableCell>
                  <span className={`text-[10px] font-mono px-2 py-1 ${STATUS_CHIP[d.status]}`}>{STATUS_LABEL[d.status]}</span>
                </TableCell>
                <TableCell className="text-right pr-5 whitespace-nowrap">
                  {canFile(d) && (
                    <button onClick={() => onPreview(d)} title="Preview file" data-testid={`preview-btn-${d.id}`}
                      className="text-slate-400 hover:text-teal-700 p-1"><Eye className="h-4 w-4 inline" /></button>
                  )}
                  {isHR && d.verification === "pending_verification" && (
                    <button onClick={() => verify(d)} title="Verify" data-testid={`verify-btn-${d.id}`}
                      className="text-amber-600 hover:text-teal-700 p-1"><BadgeCheck className="h-4 w-4 inline" /></button>
                  )}
                  {isHR && (
                    <>
                      <button onClick={() => { setEditDoc(d); setEditForm({ title: d.title, issue_date: d.issue_date || "", expiry_date: d.expiry_date || "" }); }}
                        title="Edit" data-testid={`edit-btn-${d.id}`}
                        className="text-slate-400 hover:text-slate-700 p-1"><Pencil className="h-3.5 w-3.5 inline" /></button>
                      <button onClick={() => setDeleteDoc(d)} title="Delete" data-testid={`delete-btn-${d.id}`}
                        className="text-slate-400 hover:text-red-600 p-1"><Trash2 className="h-3.5 w-3.5 inline" /></button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Dialog open={!!editDoc} onOpenChange={(o) => !o && setEditDoc(null)}>
        <DialogContent className="rounded-none max-w-sm" data-testid="edit-doc-dialog">
          <DialogHeader><DialogTitle className="text-[15px]">Edit document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Title</Label>
              <Input value={editForm.title || ""} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                className="rounded-none mt-1 text-[13px]" data-testid="edit-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Issue date</Label>
                <Input type="date" value={editForm.issue_date || ""} onChange={(e) => setEditForm((f) => ({ ...f, issue_date: e.target.value }))}
                  className="rounded-none mt-1 text-[13px]" data-testid="edit-issue-input" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Expiry date</Label>
                <Input type="date" value={editForm.expiry_date || ""} onChange={(e) => setEditForm((f) => ({ ...f, expiry_date: e.target.value }))}
                  className="rounded-none mt-1 text-[13px]" data-testid="edit-expiry-input" />
              </div>
            </div>
            <Button onClick={saveEdit} className="rounded-none w-full bg-teal-700 hover:bg-teal-800" data-testid="edit-save-btn">
              Save changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteDoc} onOpenChange={(o) => !o && setDeleteDoc(null)}>
        <DialogContent className="rounded-none max-w-sm" data-testid="delete-doc-dialog">
          <DialogHeader><DialogTitle className="text-[15px]">Delete document?</DialogTitle></DialogHeader>
          <p className="text-[13px] text-slate-600">
            This removes the metadata, the stored file and the protected number for
            "{deleteDoc?.title}" ({deleteDoc?.employee_name}). This cannot be undone.
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setDeleteDoc(null)} className="rounded-none h-8 text-[12px]">Cancel</Button>
            <Button onClick={doDelete} className="rounded-none h-8 text-[12px] bg-red-600 hover:bg-red-700" data-testid="delete-confirm-btn">
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
